import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BookMarkSimpleIcon from "../../assets/icons/bookmark-simple.svg?react";
import BookMarkFillIcon from "../../assets/icons/bookmark-fill.svg?react";
import {
  apiClient,
  getApiQueryData,
  resetApiQuery,
  setApiQueryBody,
  useApiQuery,
  useApiRevalidate,
} from "../../services/api";
import { PopoverCard, usePopoverAnchor } from "../ui/Popover";
import { displayToast, displayToastWithActions } from "../app/AppToast";
import { useFirebaseUser } from "../../domains/user/hooks/useFirebaseUser";
import { useOnboardingGuard } from "../../domains/user/hooks/useOnboardingGuard";
import type { useSaveArticle } from "../../domains/user/hooks/useSaveArticle";
import type { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import {
  convertArticleToItemData,
  gaEventArticleSave,
} from "../../tools/analytics/domains/article.analytics";
import { gaEventContentSave } from "../../tools/analytics/domains/content.analytics";
import { gaPushEvent } from "../../tools/analytics/base.analytics";
import { cn } from "../../lib/utils";
import {
  SaveToCollectionPanel,
  sameName,
  type Collection,
} from "./SaveToCollectionPanel";

type SaveableArticle = NonNullable<Parameters<typeof useSaveArticle>[0]>;

// The popover lists every collection so that search and the duplicate check
// see them all. Test accounts hold several hundred; readers far fewer.
const COLLECTIONS_QUERY = { query: { limit: 1000, offset: 0 } };

// The suggestions endpoint accepts attempts 0 to 20. Past the last one, the
// refresh button asks for that batch again rather than a rejected request.
const LAST_SUGGESTION_ATTEMPT = 20;

/**
 * What the popover shows under the bookmark (Figma "D3.save"). `naming` is the
 * "+ New collection" field once opened: `typed` is null until the reader
 * types, which lets a JuisciGPT suggestion fill the field when it lands, and
 * `attempt` asks for a fresh batch of suggestions.
 */
type Panel =
  | { kind: "closed" }
  | {
      kind: "open";
      query: string;
      naming: { typed: string | null; attempt: number } | null;
    };

/**
 * The desktop save flow, from the bookmark: a click saves the article to the
 * reader's Saved list and opens a popover to file it in collections too; a
 * click on a filled bookmark unsaves it everywhere, with an undo.
 */
function useDesktopSave(
  article: SaveableArticle,
  analyticsListName: AnalyticsItemList
) {
  const { t } = useTranslation();
  const history = useHistory();
  const { firebaseUser } = useFirebaseUser();
  const revalidate = useApiRevalidate();
  const onboardingGuard = useOnboardingGuard();
  const [panel, setPanel] = useState<Panel>({ kind: "closed" });
  const anchor = usePopoverAnchor({
    isOpen: panel.kind === "open",
    onDismiss: () => {
      setPanel({ kind: "closed" });
    },
    edge: "right",
  });

  const savedQuery = useApiQuery(
    "/webapp/private/saved",
    firebaseUser === null ? false : {}
  );
  const collectionsQuery = useApiQuery(
    "/webapp/private/playlists/me",
    panel.kind === "open" ? COLLECTIONS_QUERY : false
  );
  const naming = panel.kind === "open" ? panel.naming : null;
  const suggestionsQuery = useApiQuery(
    "/webapp/private/saved/collection-name-suggestions",
    naming === null
      ? false
      : {
          query: { slug: article.slug, attempt: naming.attempt },
          durable: true,
        }
  );

  // While the list loads, a click could save an article already saved: the
  // bookmark waits. When the list failed to load, or the reader is signed out
  // (the onboarding guard then takes over), a click saves: saving twice is
  // harmless, a dead control is not.
  const bookmark =
    savedQuery.state === "success"
      ? savedQuery.body.articleIds.includes(article.id)
        ? ("saved" as const)
        : ("unsaved" as const)
      : savedQuery.state === "pending"
        ? ("loading" as const)
        : ("unsaved" as const);
  const isSaved = bookmark === "saved";

  function setSaved(saved: boolean): void {
    setApiQueryBody("/webapp/private/saved", {}, (body) => ({
      ...body,
      articleIds: saved
        ? [article.id, ...body.articleIds.filter((id) => id !== article.id)]
        : body.articleIds.filter((id) => id !== article.id),
      listArticleIds: saved
        ? [article.id, ...body.listArticleIds.filter((id) => id !== article.id)]
        : body.listArticleIds.filter((id) => id !== article.id),
    }));
  }

  // The Saved list alone: a collection that holds the article keeps it saved,
  // which the next load of the Saved ids tells.
  function setInSavedListCache(isIn: boolean): void {
    setApiQueryBody("/webapp/private/saved", {}, (body) => ({
      ...body,
      listArticleIds: isIn
        ? [article.id, ...body.listArticleIds.filter((id) => id !== article.id)]
        : body.listArticleIds.filter((id) => id !== article.id),
    }));
  }

  // Put the article in (or out of) several cached collections at once.
  function setInCollections(collectionIds: string[], isIn: boolean): void {
    setApiQueryBody(
      "/webapp/private/playlists/me",
      COLLECTIONS_QUERY,
      (body) => ({
        ...body,
        docs: body.docs.map((collection) =>
          !collectionIds.includes(collection.id)
            ? collection
            : {
                ...collection,
                playlist: isIn
                  ? [
                      ...collection.playlist.filter((id) => id !== article.id),
                      article.id,
                    ]
                  : collection.playlist.filter((id) => id !== article.id),
              }
        ),
      })
    );
  }

  function setInCollection(collectionId: string, isIn: boolean): void {
    setInCollections([collectionId], isIn);
  }

  function refreshLibrary(): void {
    void revalidate("/webapp/private/saved");
    void revalidate("/webapp/private/playlists/me");
    void revalidate("/webapp/private/feed/saved");
  }

  function trackSave(): void {
    gaEventArticleSave({ article, listName: analyticsListName });
    gaEventContentSave(
      convertArticleToItemData({ article, listName: analyticsListName })
    );
  }

  async function save(): Promise<void> {
    if (
      !onboardingGuard(
        t("🔒 Please complete your profile first to save this content. ")
      )
    )
      return;
    setSaved(true);
    setPanel({ kind: "open", query: "", naming: null });
    try {
      await apiClient.post("/webapp/private/saved", {
        json: { kind: "article", slug: article.slug },
      });
      trackSave();
    } catch {
      setSaved(false);
      setPanel({ kind: "closed" });
      displayToastWithActions(
        t("Couldn't save this article. Check your connection."),
        "error",
        [{ label: t("Try again"), onClick: () => void save() }]
      );
    }
    refreshLibrary();
  }

  async function restore(collectionIds: string[]): Promise<void> {
    setSaved(true);
    setInCollections(collectionIds, true);
    try {
      await apiClient.post("/webapp/private/saved", {
        json: { kind: "article", slug: article.slug },
      });
      if (collectionIds.length > 0)
        await apiClient.post("/webapp/private/playlists/batch/article", {
          json: { slug: article.slug, add: collectionIds, remove: [] },
        });
    } catch {
      // Either request may have landed before the failure, so the cache cannot
      // tell which collections hold the article now. Reset it: an open popover
      // (another article's) refetches at once, a closed one loads fresh. The
      // Saved list refetches with `refreshLibrary` (the bookmark reads it).
      void resetApiQuery("/webapp/private/playlists/me", COLLECTIONS_QUERY);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
    refreshLibrary();
  }

  async function unsave(): Promise<void> {
    setPanel({ kind: "closed" });
    setSaved(false);
    // The server takes the article out of every collection too. The cached
    // ones that held it are kept to put it back if the request fails.
    const cached = getApiQueryData(
      "/webapp/private/playlists/me",
      COLLECTIONS_QUERY
    );
    const heldIn =
      cached === null
        ? []
        : cached.docs
            .filter((collection) => collection.playlist.includes(article.id))
            .map((collection) => collection.id);
    setInCollections(heldIn, false);
    try {
      const { body } = await apiClient.post("/webapp/private/saved/remove", {
        json: { kind: "article", slug: article.slug },
      });
      displayToastWithActions(
        t("Removed from Saved and your collections"),
        "info",
        [{ label: t("Undo"), onClick: () => void restore(body.playlistIds) }]
      );
    } catch {
      setSaved(true);
      setInCollections(heldIn, true);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
    refreshLibrary();
  }

  // The popover's Saved box: it files the article in the Saved list, or takes
  // it out of that list only. Its collections keep it.
  async function setInSavedList(isIn: boolean): Promise<void> {
    setInSavedListCache(isIn);
    const json = { kind: "article" as const, slug: article.slug };
    try {
      if (isIn) await apiClient.post("/webapp/private/saved", { json });
      else await apiClient.post("/webapp/private/saved/list/remove", { json });
    } catch {
      setInSavedListCache(!isIn);
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    } finally {
      refreshLibrary();
    }
    if (isIn) {
      trackSave();
      return;
    }
    displayToastWithActions(
      t("Removed from {{name}}", { name: t("Saved") }),
      "info",
      [{ label: t("Undo"), onClick: () => void setInSavedList(true) }]
    );
  }

  // `fromEnter`: the reader filed by pressing Enter in the search field, which
  // closed the popover before the request.
  async function fileIn(
    collection: Collection,
    isIn: boolean,
    fromEnter: boolean
  ): Promise<void> {
    setInCollection(collection.id, isIn);
    try {
      await apiClient.post("/webapp/private/playlists/batch/article", {
        json: {
          slug: article.slug,
          add: isIn ? [collection.id] : [],
          remove: isIn ? [] : [collection.id],
        },
      });
    } catch {
      setInCollection(collection.id, !isIn);
      // Bring the closed popover back so the reader can retry: the filled
      // bookmark would now unsave.
      if (fromEnter) setPanel({ kind: "open", query: "", naming: null });
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    } finally {
      refreshLibrary();
    }
    if (isIn) {
      trackSave();
      announceFiled(collection.id, collection.title);
      return;
    }
    displayToastWithActions(
      t("Removed from {{name}}", { name: collection.title }),
      "info",
      [
        {
          label: t("Undo"),
          onClick: () => void fileIn(collection, true, false),
        },
      ]
    );
  }

  function announceFiled(collectionId: string, name: string): void {
    displayToastWithActions(t("Saved to {{name}}", { name }), "success", [
      {
        label: t("View"),
        onClick: () => {
          history.push(`/profile/playlist/${collectionId}`);
        },
      },
      {
        label: t("Undo"),
        onClick: () => {
          setInCollection(collectionId, false);
          void apiClient
            .post("/webapp/private/playlists/batch/article", {
              json: { slug: article.slug, add: [], remove: [collectionId] },
            })
            .then(refreshLibrary, () => {
              setInCollection(collectionId, true);
              displayToast(
                t("Something went wrong. Please try again."),
                "error"
              );
            });
        },
      },
    ]);
  }

  async function create(name: string): Promise<void> {
    const title = name.trim();
    if (title === "") return;
    setPanel({ kind: "closed" });
    let createdId: string | null = null;
    try {
      const { body } = await apiClient.post("/webapp/private/playlists", {
        json: { title },
      });
      createdId = body.id;
      gaPushEvent("playlist_created", { origin: "save_popover", title });
      await apiClient.post("/webapp/private/playlists/batch/article", {
        json: { slug: article.slug, add: [body.id], remove: [] },
      });
      trackSave();
      announceFiled(body.id, title);
    } catch {
      // Reopen so the reader can try again without unsaving: on the name when
      // nothing was created, on the list when only the filing failed (the new
      // collection is there to tick, and its name would now be a duplicate).
      setPanel({
        kind: "open",
        query: "",
        naming: createdId === null ? { typed: title, attempt: 0 } : null,
      });
      // A duplicate name never gets here: `createOrFile` matches it against
      // the loaded list first, so a 409 is a race like any other failure.
      displayToast(t("Something went wrong. Please try again."), "error");
    }
    refreshLibrary();
  }

  const collections =
    collectionsQuery.state === "success" ? collectionsQuery.body.docs : [];

  // A name the reader already uses, in any case, files the article in that
  // collection rather than creating a twin: the server only refuses an exact
  // match. Nothing is created until the list has loaded.
  function createOrFile(name: string): void {
    // Without the list, an existing name would look new and make a twin.
    if (collectionsQuery.state !== "success") return;
    const match = collections.find((collection) =>
      sameName(collection.title, name)
    );
    if (match === undefined) {
      void create(name);
      return;
    }
    setPanel({ kind: "closed" });
    if (!match.playlist.includes(article.id)) void fileIn(match, true, true);
  }

  return {
    anchor,
    bookmark,
    isSaved,
    panel,
    savedCount:
      savedQuery.state === "success" ? savedQuery.body.articleIds.length : null,
    inSavedList:
      savedQuery.state === "success" &&
      savedQuery.body.listArticleIds.includes(article.id),
    collections:
      collectionsQuery.state === "success"
        ? { state: "ready" as const, docs: collections }
        : collectionsQuery.state === "error"
          ? { state: "error" as const }
          : { state: "loading" as const },
    suggestions:
      suggestionsQuery.state === "success"
        ? suggestionsQuery.body.names
        : ([] satisfies string[]),
    onBookmark: () => {
      void (isSaved ? unsave() : save());
    },
    onRetryCollections: () => {
      void collectionsQuery.refetch();
    },
    onToggleSavedList: (isIn: boolean) => {
      void setInSavedList(isIn);
    },
    onToggle: (collection: Collection) => {
      void fileIn(collection, !collection.playlist.includes(article.id), false);
    },
    onQueryChange: (query: string) => {
      setPanel({ kind: "open", query, naming: null });
    },
    onQuerySubmit: () => {
      if (panel.kind !== "open") return;
      createOrFile(panel.query);
    },
    onStartNaming: () => {
      setPanel({
        kind: "open",
        query: "",
        naming: { typed: null, attempt: 0 },
      });
    },
    onNameChange: (typed: string) => {
      if (panel.kind !== "open" || panel.naming === null) return;
      setPanel({ ...panel, naming: { ...panel.naming, typed } });
    },
    onMoreSuggestions: () => {
      if (panel.kind !== "open" || panel.naming === null) return;
      setPanel({
        ...panel,
        naming: {
          typed: null,
          attempt: Math.min(panel.naming.attempt + 1, LAST_SUGGESTION_ATTEMPT),
        },
      });
    },
    onCreate: createOrFile,
  };
}

/**
 * The bookmark of a desktop article, with the popover that files the article
 * in the reader's collections. `size` follows the surface: the article
 * header's actions are a step larger than a feed row's.
 */
export function DesktopSaveButton({
  article,
  analyticsListName,
  size,
}: {
  article: SaveableArticle;
  analyticsListName: AnalyticsItemList;
  size: "row" | "header";
}): React.JSX.Element {
  const { t } = useTranslation();
  const save = useDesktopSave(article, analyticsListName);
  const { panel } = save;

  return (
    <>
      <button
        ref={save.anchor.triggerRef}
        type="button"
        aria-label={save.isSaved ? t("Saved") : t("Save")}
        aria-pressed={save.isSaved}
        aria-expanded={panel.kind === "open"}
        title={save.isSaved ? t("Saved") : t("Save")}
        disabled={save.bookmark === "loading"}
        onClick={(event) => {
          // A feed row opens the article on click.
          event.stopPropagation();
          save.onBookmark();
        }}
        className={cn(
          "flex cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 text-ds-black transition-colors hover:bg-ds-slate-100 disabled:cursor-default disabled:opacity-40 [&>svg]:size-5",
          size === "header" ? "size-8" : "size-7"
        )}
      >
        {save.isSaved ? <BookMarkFillIcon /> : <BookMarkSimpleIcon />}
      </button>
      {panel.kind === "open" ? (
        // The card is portalled, but React still bubbles its events up this
        // tree: a click in it, or on its backdrop, would reach a feed row's
        // own click and open the article.
        <span
          className="contents"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <PopoverCard
            anchor={save.anchor}
            role={null}
            className="w-80 rounded-xl p-2"
          >
            <SaveToCollectionPanel
              query={panel.query}
              savedCount={save.savedCount}
              inSavedList={save.inSavedList}
              collections={save.collections}
              articleId={article.id}
              naming={
                panel.naming === null
                  ? null
                  : {
                      typed: panel.naming.typed,
                      suggestions: save.suggestions,
                    }
              }
              onQueryChange={save.onQueryChange}
              onQuerySubmit={save.onQuerySubmit}
              onToggleSavedList={save.onToggleSavedList}
              onToggle={save.onToggle}
              onStartNaming={save.onStartNaming}
              onRetryCollections={save.onRetryCollections}
              onNameChange={save.onNameChange}
              onMoreSuggestions={save.onMoreSuggestions}
              onCreate={save.onCreate}
            />
          </PopoverCard>
        </span>
      ) : null}
    </>
  );
}
